import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  formatAuthenticationDetailsSummary,
  parseAuthenticationDetailsStreamOutput,
} from '@terra/shared-dto-chat-types';
import {
  readAskUserAnswer,
  readAskUserPrompt,
} from '../../components/tools/ask-user/ask-user-answer-card-view.utils';
import {
  isRenderableToolName,
  parseChatHistory,
  type PlanTodoItem,
  type TraceMessage,
  type TraceMessageContent,
  type TraceToolCall,
} from '../types';
import {
  extractCoderMarkdownResult,
  resolveCoderDisplayResult,
} from './coder-tool-output';
import {
  isCoPilotLabelOnlyLookupTool,
  shouldShowCoPilotInlineToolArgsPreview,
} from './co-pilot-trace-display';
import { formatHttpExchangePayloads } from './http-exchange-output.utils';
import { extractReadFilePath, toTraceReadFileEntry } from './read-file-tool';
import { getToolCategory } from './tool-categories';
import {
  getToolDisplayName,
  getTraceToolRenderConfig,
  isHttpExchangeTool,
  normalizeToolName,
} from './tool-config.helpers';
import { usesReadToolCard } from './trace-tool-filters';

export type CoPilotTraceExportStatus =
  | 'running'
  | 'completed'
  | 'error'
  | 'aborted';

export type CoPilotTraceExportUserEntry = {
  kind: 'user_message';
  text: string;
  timestamp?: string;
};

export type CoPilotTraceExportAssistantEntry = {
  kind: 'assistant_message';
  text: string;
  timestamp?: string;
};

export type CoPilotTraceExportReadFileEntry = {
  displayName: string;
  filePath?: string;
  content: string;
  error?: string;
};

export type CoPilotTraceExportReadFilesEntry = {
  kind: 'read_files';
  files: CoPilotTraceExportReadFileEntry[];
  timestamp?: string;
};

export type CoPilotTraceExportCodeExecutionEntry = {
  kind: 'code_execution';
  displayName: string;
  status: TraceToolCall['status'];
  durationMs?: number;
  code?: string;
  result?: string;
  error?: string;
  timestamp?: string;
};

export type CoPilotTraceExportCodeExecutionsEntry = {
  kind: 'code_executions';
  executions: CoPilotTraceExportCodeExecutionEntry[];
  timestamp?: string;
};

export type CoPilotTraceExportAskUserEntry = {
  kind: 'ask_user';
  prompt: string;
  answer?: string;
  skipped?: boolean;
  status: TraceToolCall['status'];
  timestamp?: string;
};

export type CoPilotTraceExportHttpRequestEntry = {
  kind: 'http_request';
  displayName: string;
  status: TraceToolCall['status'];
  durationMs?: number;
  request?: string;
  response?: string;
  error?: string;
  timestamp?: string;
};

export type CoPilotTraceExportPlanUpdateEntry = {
  kind: 'plan_update';
  todos: PlanTodoItem[];
  status: TraceToolCall['status'];
  timestamp?: string;
};

export type CoPilotTraceExportToolEntry = {
  kind: 'tool';
  displayName: string;
  toolName: string;
  status: TraceToolCall['status'];
  durationMs?: number;
  labelOnly?: boolean;
  args?: Record<string, unknown>;
  output?: string;
  error?: string;
  timestamp?: string;
};

export type CoPilotTraceTimelineEntry =
  | CoPilotTraceExportUserEntry
  | CoPilotTraceExportAssistantEntry
  | CoPilotTraceExportReadFilesEntry
  | CoPilotTraceExportCodeExecutionsEntry
  | CoPilotTraceExportCodeExecutionEntry
  | CoPilotTraceExportAskUserEntry
  | CoPilotTraceExportHttpRequestEntry
  | CoPilotTraceExportPlanUpdateEntry
  | CoPilotTraceExportToolEntry;

export type CoPilotTraceExportPayload = {
  formatVersion: 1;
  threadId: string;
  applicationId?: string;
  tracePhase: 'co-pilot';
  status: CoPilotTraceExportStatus;
  exportedAt: string;
  timeline: CoPilotTraceTimelineEntry[];
};

type ToolCallContent = Extract<TraceMessageContent, { type: 'tool_call' }>;

type AssistantRenderItem =
  | { type: 'single'; content: TraceMessageContent }
  | { type: 'python-group'; contents: ToolCallContent[] }
  | { type: 'read-group'; contents: ToolCallContent[] };

function filterOutEndTest(messages: TraceMessage[]): TraceMessage[] {
  return messages
    .map((message) => ({
      ...message,
      content: message.content.filter((content) => {
        if (
          content.type === 'tool_call' &&
          normalizeToolName(content.toolCall.name) === 'end-test'
        ) {
          return false;
        }
        return true;
      }),
    }))
    .filter((message) => message.content.length > 0);
}

function filterExportMessages(messages: TraceMessage[]): TraceMessage[] {
  return filterOutEndTest(messages).filter((message) =>
    message.content.some((content) => {
      if (content.type === 'text') {
        return content.text.trim().length > 0;
      }
      if (content.type === 'tool_call') {
        return shouldRenderToolCallContent(content);
      }
      if (content.type === 'reasoning') {
        return content.text.trim().length > 0;
      }
      return false;
    }),
  );
}

function shouldRenderToolCallContent(content: ToolCallContent): boolean {
  if (!isRenderableToolName(content.toolCall.name, 'co-pilot')) {
    return false;
  }

  const normalizedName = normalizeToolName(content.toolCall.name);

  if (normalizedName === 'todo-write') {
    return content.toolCall.args?.merge === true;
  }

  return getToolCategory(content.toolCall.name) !== 'end-test';
}

function shouldRenderContent(content: TraceMessageContent): boolean {
  switch (content.type) {
    case 'text':
    case 'reasoning':
      return content.text.trim().length > 0;
    case 'tool_call':
      return shouldRenderToolCallContent(content);
    case 'tool_result':
      return false;
    default:
      return false;
  }
}

function isReadToolCallForGrouping(content: ToolCallContent): boolean {
  return normalizeToolName(content.toolCall.name) === 'read-file';
}

function isPythonExecutionToolCall(content: ToolCallContent): boolean {
  const normalizedName = normalizeToolName(content.toolCall.name);
  if (getToolCategory(content.toolCall.name) !== 'code-execution') {
    return false;
  }

  return (
    normalizedName === 'coder' ||
    normalizedName === 'coder-tool' ||
    normalizedName === 'python-tool' ||
    normalizedName === 'sub-agent-coder'
  );
}

function getAssistantRenderItems(
  contents: TraceMessageContent[],
): AssistantRenderItem[] {
  const items: AssistantRenderItem[] = [];
  let currentPythonGroup: ToolCallContent[] = [];
  let currentReadGroup: ToolCallContent[] = [];

  const flushPythonGroup = () => {
    if (currentPythonGroup.length === 0) {
      return;
    }

    if (currentPythonGroup.length === 1 && currentPythonGroup[0]) {
      items.push({ type: 'single', content: currentPythonGroup[0] });
    } else {
      items.push({ type: 'python-group', contents: currentPythonGroup });
    }

    currentPythonGroup = [];
  };

  const flushReadGroup = () => {
    if (currentReadGroup.length === 0) {
      return;
    }

    items.push({ type: 'read-group', contents: currentReadGroup });
    currentReadGroup = [];
  };

  for (const content of contents) {
    if (!shouldRenderContent(content)) {
      continue;
    }

    if (content.type === 'tool_call' && isReadToolCallForGrouping(content)) {
      flushPythonGroup();
      currentReadGroup.push(content);
      continue;
    }

    if (content.type === 'tool_call' && isPythonExecutionToolCall(content)) {
      flushReadGroup();
      currentPythonGroup.push(content);
      continue;
    }

    flushPythonGroup();
    flushReadGroup();
    items.push({ type: 'single', content });
  }

  flushPythonGroup();
  flushReadGroup();
  return items;
}

function readCoderInput(args: Record<string, unknown> | undefined): {
  script?: string;
  code?: string;
  query?: string;
} {
  if (!args) {
    return {};
  }

  return {
    script: typeof args.script === 'string' ? args.script : undefined,
    code: typeof args.code === 'string' ? args.code : undefined,
    query: typeof args.query === 'string' ? args.query : undefined,
  };
}

function readCoderRuntimeMs(toolCall: TraceToolCall): number | undefined {
  if (toolCall.timing?.durationMs !== undefined) {
    return toolCall.timing.durationMs;
  }

  if (typeof toolCall.output !== 'string') {
    return undefined;
  }

  try {
    const parsed = JSON.parse(toolCall.output) as {
      output?: { codeRuntime?: number };
    };
    return parsed.output?.codeRuntime;
  } catch {
    return undefined;
  }
}

function resolveCoderExportFields(toolCall: TraceToolCall): {
  code: string;
  result: string;
  runtimeMs?: number;
} | null {
  const input = readCoderInput(toolCall.args);
  const fromOutput =
    typeof toolCall.output === 'string'
      ? resolveCoderDisplayResult({
          output: toolCall.output,
          message: toolCall.streamMessage,
        })
      : resolveCoderDisplayResult({
          output: toolCall.output,
          message: toolCall.streamMessage,
        });

  let code = input.script || input.code || input.query || '';

  if (!code && typeof toolCall.output === 'string') {
    try {
      const parsed = JSON.parse(toolCall.output) as {
        script?: string;
        code?: string;
      };
      code =
        (typeof parsed.script === 'string' ? parsed.script : '') ||
        (typeof parsed.code === 'string' ? parsed.code : '');
    } catch {
      // ignore malformed coder payload
    }
  }

  const result =
    fromOutput ||
    (typeof toolCall.output === 'string'
      ? extractCoderMarkdownResult(toolCall.output)
      : '') ||
    '';

  if (!code && !result) {
    return null;
  }

  return {
    code,
    result,
    runtimeMs: readCoderRuntimeMs(toolCall),
  };
}

function formatNavigateWebOutput(output: string): string {
  try {
    const parsed: unknown = JSON.parse(output);
    if (typeof parsed !== 'object' || parsed === null) {
      return output;
    }

    const record = parsed as Record<string, unknown>;
    if (typeof record.result === 'string' && record.result.trim().length > 0) {
      return record.result.trim();
    }
  } catch {
    return output;
  }

  return output;
}

function formatToolOutputForExport(
  toolCall: TraceToolCall,
): string | undefined {
  const normalizedName = normalizeToolName(toolCall.name);
  const output = toolCall.output?.trim();

  if (!output) {
    return undefined;
  }

  if (normalizedName === 'navigate-web') {
    return formatNavigateWebOutput(output);
  }

  if (normalizedName === 'get-authentication-details') {
    const parsed = parseAuthenticationDetailsStreamOutput(output);
    if (parsed) {
      return formatAuthenticationDetailsSummary(parsed);
    }
  }

  if (normalizedName === 'render-visualization') {
    try {
      const parsed: unknown = JSON.parse(output);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        (parsed as { type?: string }).type === 'vega_lite_visualization' &&
        typeof (parsed as { title?: string }).title === 'string'
      ) {
        const title = (parsed as { title: string }).title;
        const description = (parsed as { description?: string }).description;
        return description
          ? `Chart: ${title} — ${description}`
          : `Chart: ${title}`;
      }
    } catch {
      return output;
    }
  }

  try {
    const parsed: unknown = JSON.parse(output);
    if (typeof parsed === 'object' && parsed !== null) {
      return JSON.stringify(parsed, null, 2);
    }
  } catch {
    return output;
  }

  return output;
}

function serializeCodeExecution(
  toolCall: TraceToolCall,
  timestamp?: string,
): CoPilotTraceExportCodeExecutionEntry {
  const displayName = getToolDisplayName(
    toolCall.name,
    toolCall.args,
    'co-pilot',
  );
  const coderFields = resolveCoderExportFields(toolCall);

  return {
    kind: 'code_execution',
    displayName,
    status: toolCall.status,
    durationMs: coderFields?.runtimeMs ?? toolCall.timing?.durationMs,
    code: coderFields?.code,
    result: coderFields?.result,
    error: toolCall.error,
    timestamp,
  };
}

function serializeReadFiles(
  toolCalls: TraceToolCall[],
  timestamp?: string,
): CoPilotTraceExportReadFilesEntry {
  return {
    kind: 'read_files',
    timestamp,
    files: toolCalls.map((toolCall) => {
      const entry = toTraceReadFileEntry(toolCall, { tracePhase: 'co-pilot' });
      return {
        displayName: entry.displayName,
        filePath: entry.filePath,
        content: entry.output,
        error: entry.error,
      };
    }),
  };
}

function serializeToolCall(
  toolCall: TraceToolCall,
  timestamp?: string,
): CoPilotTraceTimelineEntry | CoPilotTraceTimelineEntry[] | null {
  const normalizedName = normalizeToolName(toolCall.name);
  const displayName = getToolDisplayName(
    toolCall.name,
    toolCall.args,
    'co-pilot',
  );

  if (normalizedName === 'todo-write' && toolCall.args?.merge === true) {
    const todos = Array.isArray(toolCall.args.todos)
      ? (toolCall.args.todos as PlanTodoItem[])
      : [];
    return {
      kind: 'plan_update',
      todos,
      status: toolCall.status,
      timestamp,
    };
  }

  if (normalizedName === 'ask-user') {
    if (toolCall.status !== 'completed') {
      return null;
    }

    const prompt = readAskUserPrompt(toolCall.args);
    const { answer, skipped } = readAskUserAnswer(toolCall.output);

    return {
      kind: 'ask_user',
      prompt,
      answer: skipped ? undefined : answer,
      skipped,
      status: toolCall.status,
      timestamp,
    };
  }

  if (isHttpExchangeTool(toolCall.name)) {
    const { requestPayload, responsePayload, error } =
      formatHttpExchangePayloads(toolCall.args, toolCall.output ?? '');

    return {
      kind: 'http_request',
      displayName,
      status: toolCall.status,
      durationMs: toolCall.timing?.durationMs,
      request: requestPayload,
      response: responsePayload,
      error: error ?? toolCall.error,
      timestamp,
    };
  }

  if (
    normalizedName === 'coder' ||
    normalizedName === 'coder-tool' ||
    normalizedName === 'python-tool' ||
    normalizedName === 'sub-agent-coder'
  ) {
    return serializeCodeExecution(toolCall, timestamp);
  }

  if (usesReadToolCard(toolCall.name, 'co-pilot')) {
    return serializeReadFiles([toolCall], timestamp);
  }

  const readFilePath = extractReadFilePath(toolCall.args);
  const isCoPilotLabelOnly = isCoPilotLabelOnlyLookupTool({
    toolName: toolCall.name,
    tracePhase: 'co-pilot',
    filePath:
      readFilePath ??
      toTraceReadFileEntry(toolCall, { tracePhase: 'co-pilot' }).filePath,
  });
  const renderConfig = getTraceToolRenderConfig(toolCall.name);
  const showCoPilotLookupArgs = shouldShowCoPilotInlineToolArgsPreview({
    toolName: toolCall.name,
    tracePhase: 'co-pilot',
  });
  const hasArgs =
    Boolean(toolCall.args && Object.keys(toolCall.args).length > 0) &&
    !renderConfig.hideArgs &&
    !isCoPilotLabelOnly &&
    showCoPilotLookupArgs;
  const hasResult =
    Boolean(toolCall.output?.trim() || toolCall.error) && !isCoPilotLabelOnly;
  const labelOnly = isCoPilotLabelOnly || (!hasArgs && !hasResult);

  return {
    kind: 'tool',
    displayName,
    toolName: toolCall.name,
    status: toolCall.status,
    durationMs: toolCall.timing?.durationMs,
    labelOnly,
    args: hasArgs ? toolCall.args : undefined,
    output: hasResult ? formatToolOutputForExport(toolCall) : undefined,
    error: toolCall.error,
    timestamp,
  };
}

function serializeAssistantRenderItem(
  item: AssistantRenderItem,
  timestamp?: string,
): CoPilotTraceTimelineEntry[] {
  if (item.type === 'read-group') {
    return [
      serializeReadFiles(
        item.contents.map((content) => content.toolCall),
        timestamp,
      ),
    ];
  }

  if (item.type === 'python-group') {
    if (item.contents.length === 1 && item.contents[0]) {
      const serialized = serializeCodeExecution(
        item.contents[0].toolCall,
        timestamp,
      );
      return [serialized];
    }

    return [
      {
        kind: 'code_executions',
        timestamp,
        executions: item.contents.map((content) =>
          serializeCodeExecution(content.toolCall, timestamp),
        ),
      },
    ];
  }

  if (item.content.type === 'text') {
    return [
      {
        kind: 'assistant_message',
        text: item.content.text,
        timestamp,
      },
    ];
  }

  if (item.content.type === 'tool_call') {
    const serialized = serializeToolCall(item.content.toolCall, timestamp);
    if (!serialized) {
      return [];
    }
    return Array.isArray(serialized) ? serialized : [serialized];
  }

  return [];
}

function buildTimeline(messages: TraceMessage[]): CoPilotTraceTimelineEntry[] {
  const timeline: CoPilotTraceTimelineEntry[] = [];

  for (const message of messages) {
    if (message.role === 'user') {
      const text = message.content
        .filter((content) => content.type === 'text')
        .map((content) => (content.type === 'text' ? content.text : ''))
        .join('')
        .trim();

      if (text) {
        timeline.push({
          kind: 'user_message',
          text,
          timestamp: message.timestamp,
        });
      }
      continue;
    }

    if (message.role !== 'assistant') {
      continue;
    }

    const items = getAssistantRenderItems(message.content);
    for (const item of items) {
      timeline.push(...serializeAssistantRenderItem(item, message.timestamp));
    }
  }

  return timeline;
}

const DOWNLOAD_EXCLUDED_TRACE_TIMELINE_KINDS = new Set<
  CoPilotTraceTimelineEntry['kind']
>([
  'tool',
  'read_files',
  'code_execution',
  'code_executions',
  'plan_update',
  'ask_user',
]);

export function sanitizeCoPilotTraceExportPayloadForDownload({
  exportPayload,
}: {
  exportPayload: CoPilotTraceExportPayload;
}): CoPilotTraceExportPayload {
  return {
    ...exportPayload,
    timeline: exportPayload.timeline.filter(
      (entry) => !DOWNLOAD_EXCLUDED_TRACE_TIMELINE_KINDS.has(entry.kind),
    ),
  };
}

export function buildCoPilotTraceExportView({
  threadId,
  applicationId,
  messages,
  status,
}: {
  threadId: string;
  applicationId?: string;
  messages: TerraUIMessage[];
  status: CoPilotTraceExportStatus;
}): CoPilotTraceExportPayload {
  const parsedMessages = parseChatHistory(messages, 'co-pilot');
  const exportMessages = filterExportMessages(parsedMessages);

  return {
    formatVersion: 1,
    threadId,
    applicationId,
    tracePhase: 'co-pilot',
    status,
    exportedAt: new Date().toISOString(),
    timeline: buildTimeline(exportMessages),
  };
}
